import React, { useContext, useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import PropertyContext from "../../contexts/PropertyContext";
import { useAuth } from "../../AuthContext";
import { drawReportHeaderLib } from "../../utils/reports/pdfChromeLib";
import { downloadBlob } from "../../utils/reports/downloadBlob";
import { buildReportFilename } from "../../utils/reports/reportFilename";

const GenerateSDSReport = () => {
  const { items } = useContext(PropertyContext);
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);

  const generateSDSReport = async () => {
    setLoading(true);
    try {
      // Filter items with SDS URLs and sort alphabetically
      const itemsWithSDS = items
        .filter((item) => item.sdsURL && item.sdsURL.trim() !== "")
        .sort((a, b) => {
          const nameA = (a.itemName || "").toLowerCase();
          const nameB = (b.itemName || "").toLowerCase();
          return nameA.localeCompare(nameB);
        });

      if (itemsWithSDS.length === 0) {
        alert("No items with SDS URLs found");
        setLoading(false);
        return;
      }

      console.log(`Found ${itemsWithSDS.length} items with SDS URLs`);

      // Create a new PDF document
      const pdfDoc = await PDFDocument.create();
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      // Track page numbers for TOC
      const tocEntries = [];
      let currentPageNumber = 3; // Cover page (1) + TOC will start at page 2

      // Create cover page with standardized header
      const coverPage = pdfDoc.addPage([612, 792]); // Letter size

      const contentTop = drawReportHeaderLib(coverPage, {
        title: "Safety Data Sheets",
        generatedBy:
          currentUser?.displayName || currentUser?.email || "Unknown",
        font,
        boldFont,
      });

      coverPage.drawText(`Total Chemicals: ${itemsWithSDS.length}`, {
        x: 50,
        y: contentTop,
        size: 14,
        font: font,
        color: rgb(0.3, 0.3, 0.3),
      });

      // Fetch and embed SDS PDFs, tracking their page numbers
      console.log("Fetching SDS PDFs...");
      for (const item of itemsWithSDS) {
        try {
          console.log(`Fetching SDS for ${item.itemName}...`);

          // Fetch the SDS PDF
          const response = await fetch(item.sdsURL);
          if (!response.ok) {
            console.error(
              `Failed to fetch SDS for ${item.itemName}: ${response.statusText}`
            );
            continue;
          }

          const sdsBytes = await response.arrayBuffer();
          const sdsPdf = await PDFDocument.load(sdsBytes);

          // Record the starting page for this SDS
          const startPage = currentPageNumber;
          tocEntries.push({
            name: item.itemName,
            page: startPage,
          });

          // Copy all pages from this SDS
          const copiedPages = await pdfDoc.copyPages(
            sdsPdf,
            sdsPdf.getPageIndices()
          );
          copiedPages.forEach((page) => pdfDoc.addPage(page));

          // Update current page number
          currentPageNumber += copiedPages.length;

          console.log(
            `Added ${copiedPages.length} pages for ${item.itemName} (starts at page ${startPage})`
          );
        } catch (error) {
          console.error(`Error processing SDS for ${item.itemName}:`, error);
        }
      }

      // Create Table of Contents page(s)
      console.log("Creating table of contents...");
      const tocPages = [];
      let tocPage = pdfDoc.insertPage(1, [612, 792]); // Insert after cover
      tocPages.push(tocPage);

      const tocHeight = tocPage.getSize().height;
      const lineHeight = 20;
      const maxLinesPerPage = Math.floor((tocHeight - 150) / lineHeight);
      let currentLine = 0;
      let currentTocPage = 0;

      // TOC Header
      tocPages[currentTocPage].drawText("Table of Contents", {
        x: 50,
        y: tocHeight - 80,
        size: 24,
        font: boldFont,
        color: rgb(0, 0, 0),
      });

      let yPosition = tocHeight - 130;

      // Add TOC entries
      for (const entry of tocEntries) {
        if (currentLine >= maxLinesPerPage) {
          // Create new TOC page
          currentTocPage++;
          tocPage = pdfDoc.insertPage(currentTocPage, [612, 792]); //removed +1 due to off by one error
          tocPages.push(tocPage);
          yPosition = tocHeight - 80;
          currentLine = 0;
        }

        // Draw item name (truncate if too long)
        const itemName =
          entry.name.length > 60
            ? entry.name.substring(0, 57) + "..."
            : entry.name;
        tocPages[currentTocPage].drawText(itemName, {
          x: 50,
          y: yPosition,
          size: 11,
          font: font,
          color: rgb(0, 0, 0),
        });

        // Draw dotted line
        const dots = ".".repeat(80);
        tocPages[currentTocPage].drawText(dots, {
          x: 50,
          y: yPosition,
          size: 11,
          font: font,
          color: rgb(0.7, 0.7, 0.7),
        });

        // Draw page number (adjusted for TOC pages)
        const adjustedPageNum = entry.page + tocPages.length;
        tocPages[currentTocPage].drawText(adjustedPageNum.toString(), {
          x: 550,
          y: yPosition,
          size: 11,
          font: font,
          color: rgb(0, 0, 0),
        });

        yPosition -= lineHeight;
        currentLine++;
      }

      // Save and download
      console.log("Saving PDF...");
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      downloadBlob(blob, buildReportFilename("SDS_Report", { ext: "pdf" }));

      console.log("SDS Report generated successfully!");
    } catch (error) {
      console.error("Error generating SDS report:", error);
      alert("Error generating SDS report. Check console for details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="contained"
      onClick={generateSDSReport}
      disabled={loading}
      startIcon={loading ? <CircularProgress size={20} /> : null}
      fullWidth
    >
      {loading ? "Generating..." : "Generate SDS Report"}
    </Button>
  );
};

export default GenerateSDSReport;
